import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import { Alert, Grid, Typography } from '@mui/material';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import TextInput from 'src/components/UI/input/TextInput';
import DateInput from 'src/components/UI/input/DateInput';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedField,
  useChangedFields
} from 'src/components/UI/input/changed-field';
import { toStoredDate } from 'src/util/util';
import { toLocalDate } from 'src/util/partialDate';
import {
  MEASUREMENT_LABELS,
  SMOKING_OPTIONS,
  bmiOf,
  measurementProblemText,
  ratioOf
} from '../../qrisk3/measurementsText';

// The readings a form holds, in screen order (TGCLOUD-1902's count is the
// number drawn gold).
const NUMBER_FIELDS = [
  'systolicBp',
  'diastolicBp',
  'totalCholesterol',
  'hdlCholesterol',
  'cholesterolHdlRatio',
  'heightCm',
  'weightKg',
  'townsendScore'
];
const FIELDS = ['measuredOn', ...NUMBER_FIELDS, 'smokingStatus'];

const asText = (v) => (v === null || v === undefined ? '' : String(v));

// A record's readings as the form holds them. A ratio the server worked out
// from the two cholesterol values is left blank, so editing either value
// works it out again rather than contradicting it.
function formValuesOf(measurements) {
  const m = measurements || {};
  const values = {
    measuredOn: m.measuredOn || toStoredDate(new Date()),
    smokingStatus: m.smokingStatus || ''
  };
  NUMBER_FIELDS.forEach((f) => {
    values[f] = asText(m[f]);
  });
  if (m.ratioCalculated) values.cholesterolHdlRatio = '';
  return values;
}

/**
 * Clinical measurements (TGCLOUD-1911): one occasion's readings for one
 * person, recorded as a risk record of their own (POST /risk-factor/measurements).
 * Each save is a new record; opened from a record's tile, it edits that one.
 *
 * Props as PrsForm: data (the selected person), model (a record to edit),
 * onSaved, disabled, onChangeCount(unsaved, shown); the Risk Factor modal's
 * Save calls the `submit()` handle.
 */
const ClinicalMeasurementsForm = forwardRef(function ClinicalMeasurementsForm(
  { data, model, onSaved, disabled = false, onChangeCount },
  ref
) {
  const t = useAutoTranslation();
  const { setSnack } = useContext(SnackContext);
  const { content } = useContext(AppContext);
  const user = content?.pedigreeSelectedUsers?.selectedUser;
  const userId = user?.user_id || user?._id;
  // Canonical member key: the risk records' own (never the literal 'proband').
  const memberId = data?.proBandId ?? data?.familyMemberId;

  const editing =
    model?._id && model?.recordType === 'measurements' ? model : null;
  const [values, setValues] = useState(() =>
    formValuesOf(editing?.measurements)
  );
  const [problems, setProblems] = useState([]);
  const [saving, setSaving] = useState(false);

  const changes = useChangedFields({
    baseline: values,
    values,
    mode: editing ? 'edit' : 'create',
    fields: FIELDS
  });
  const { resetBaseline } = changes;
  useEffect(() => {
    onChangeCount?.(changes.unsavedCount, changes.count);
  }, [changes.unsavedCount, changes.count]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onChangeCount?.(0, 0), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Opened from another record's tile: start from that record.
  useEffect(() => {
    const loaded = formValuesOf(editing?.measurements);
    setValues(loaded);
    resetBaseline(loaded);
    setProblems([]);
  }, [editing?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setProblems((prev) => prev.filter((p) => p.field !== name && p.field));
  };
  const handleChange = (e) => set(e.target.name, e.target.value);

  const bmi = useMemo(
    () => bmiOf(values.heightCm, values.weightKg),
    [values.heightCm, values.weightKg]
  );
  const workedRatio = useMemo(
    () => ratioOf(values.totalCholesterol, values.hdlCholesterol),
    [values.totalCholesterol, values.hdlCholesterol]
  );

  const problemFor = (field) => problems.find((p) => p.field === field);
  const errorText = (field) => {
    const p = problemFor(field);
    return p ? measurementProblemText(p, t) : undefined;
  };
  const general = problems.filter((p) => !p.field || !FIELDS.includes(p.field));

  const submit = () => {
    if (!userId || !memberId) {
      setSnack({
        status: true,
        msg: t('Please save the pedigree first'),
        severity: 'error'
      });
      return Promise.resolve();
    }
    setSaving(true);
    // The picker's value is a local day; stored as 'YYYY-MM-DD' (CLAUDE.md, Dates).
    const day = toStoredDate(values.measuredOn);
    const measurements = {
      measuredOn: typeof day === 'string' ? day : '',
      smokingStatus: values.smokingStatus || null
    };
    NUMBER_FIELDS.forEach((f) => {
      measurements[f] = values[f] === '' ? null : values[f];
    });
    return axios
      .post(APICONSTANTS.saveRiskMeasurements, {
        recordId: editing?._id,
        user_id: userId,
        familyId: user?.familyId,
        familyMemberId: memberId,
        measurements
      })
      .then(({ data: saved }) => {
        setProblems([]);
        resetBaseline(values);
        setSnack({
          status: true,
          msg: t('Clinical measurements recorded'),
          severity: 'success'
        });
        onSaved?.(saved);
      })
      .catch((err) => {
        const listed = err?.response?.data?.problems;
        if (Array.isArray(listed) && listed.length) {
          setProblems(listed);
          return;
        }
        setSnack({
          status: true,
          msg: t('The measurements could not be recorded.'),
          severity: 'error'
        });
      })
      .finally(() => setSaving(false));
  };

  useImperativeHandle(ref, () => ({ submit, saving }));

  const numberInput = (field, size = { xs: 12, sm: 6 }, extra = {}) => (
    <ChangedField
      component={Grid}
      size={size}
      name={field}
      changed={changes.changed[field]}
      error={Boolean(problemFor(field))}
    >
      <TextInput
        label={MEASUREMENT_LABELS[field]}
        type="number"
        name={field}
        value={values[field]}
        onChange={handleChange}
        disabled={disabled}
        error={Boolean(problemFor(field))}
        helperText={errorText(field) || extra.helperText}
      />
    </ChangedField>
  );

  return (
    <Grid container spacing={2}>
      {general.length > 0 && (
        <Grid size={12}>
          <Alert severity="error">
            {general.map((p) => (
              <div key={p.code}>{measurementProblemText(p, t)}</div>
            ))}
          </Alert>
        </Grid>
      )}
      <ChangedField
        component={Grid}
        size={{ xs: 12, sm: 6 }}
        name="measuredOn"
        changed={changes.changed.measuredOn}
        error={Boolean(problemFor('measuredOn'))}
      >
        <FormLabel required>Measured on</FormLabel>
        <DateInput
          liveText
          value={
            typeof values.measuredOn === 'string'
              ? toLocalDate(values.measuredOn)
              : values.measuredOn
          }
          disableFuture
          onChange={(val) => set('measuredOn', val || '')}
          variant="outline"
          error={Boolean(problemFor('measuredOn'))}
          helperText={errorText('measuredOn')}
          readOnly={disabled}
        />
      </ChangedField>
      <Grid size={{ xs: 12, sm: 6 }} />
      {numberInput('systolicBp')}
      {numberInput('diastolicBp')}
      {numberInput('totalCholesterol', { xs: 12, sm: 4 })}
      {numberInput('hdlCholesterol', { xs: 12, sm: 4 })}
      {numberInput(
        'cholesterolHdlRatio',
        { xs: 12, sm: 4 },
        {
          helperText:
            values.cholesterolHdlRatio === '' && workedRatio !== null ? (
              <>
                {t('Worked out from the cholesterol values: {{ratio}}', {
                  ratio: workedRatio.toFixed(2)
                })}
              </>
            ) : undefined
        }
      )}
      {numberInput('heightCm', { xs: 12, sm: 4 })}
      {numberInput('weightKg', { xs: 12, sm: 4 })}
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormLabel>BMI</FormLabel>
        <Typography variant="body1" sx={{ mt: 1 }}>
          {bmi !== null ? bmi.toFixed(1) : '—'}
        </Typography>
      </Grid>
      <ChangedField
        component={Grid}
        size={{ xs: 12, sm: 6 }}
        name="smokingStatus"
        changed={changes.changed.smokingStatus}
        options={SMOKING_OPTIONS}
        error={Boolean(problemFor('smokingStatus'))}
      >
        <TextInput
          label={MEASUREMENT_LABELS.smokingStatus}
          type="select"
          name="smokingStatus"
          value={values.smokingStatus}
          options={SMOKING_OPTIONS}
          onChange={handleChange}
          disabled={disabled}
        />
        {/* SelectInput draws a helper only in error, and then only a string it
            translates again (its fallback is "Please select a valid field"),
            so the problem goes beneath it here. */}
        {problemFor('smokingStatus') && (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'error.main', mt: '3px', mx: '14px' }}
          >
            {errorText('smokingStatus')}
          </Typography>
        )}
      </ChangedField>
      {numberInput(
        'townsendScore',
        { xs: 12, sm: 6 },
        {
          helperText: t(
            'UK only: the deprivation score of the area the person lives in. Higher is more deprived.'
          )
        }
      )}
      {saving && (
        <Grid size={12}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('Saving…')}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
});

export default ClinicalMeasurementsForm;
